import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import { Responsive, WidthProvider } from 'react-grid-layout';
import Switch from 'react-switch';
import css from 'styled-jsx/css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faCircleNotch,
  faPencilAlt,
  faPlus,
  faSearch,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from 'reactstrap';

import SessionStorageService from '../../Services/SessionStorageService';
import WidgetUpdateHelper from '../../Lib/WidgetUpdateHelper';

import {
  DASHBOARD,
  DASHBOARD_CREATE,
  DASHBOARD_READ,
  DASHBOARD_UPDATE,
  DASHBOARD_DELETE,
  WIDGET_CREATE,
  WIDGET_GRID_UPDATE,
} from '../../Constants/RBAC';
import BrandingContext from '../../Context/BrandingContext';
import CurrentOrgContext from '../../Context/CurrentOrgContext';
import { SocketContext } from '../../Context/DashboardSocketContext';

import WidgetDisplayContainer from './WidgetDisplayContainer';
import { NoItemsExist } from '../NoDataIndication';
import Can from '../../RBAC/Can';
import CreateTemplateDashboardModal from './CreateTemplateDashboardModal';
import CreateWidgetModal from './CreateWidgetModal';
import DeleteTemplateDashboardModal from './DeleteTemplateDashboardModal';
import ViewTemplateDashboardsModal from './ViewTemplateDashboardsModal';
import EditDashboardModal from './EditDashboardModal';
import AddDashboardUsersModal from './AddDashboardUsersModal';

import TimeRangeDropwdown from './AssetTimeFilter/TimeRangeDropdown';
import { isTimeFilterVisible } from './AssetTimeFilter/utils';
import CustomTimeFilterModal from './AssetTimeFilter/CustomTimeFilterModal';

import subHours from 'date-fns/subHours';

export default memo(AssetDashboardDisplay);

const ResponsiveGridLayout = WidthProvider(Responsive);

const { className: menuToggleClass, styles: menuToggleStyles } = css.resolve`
  button {
    min-width: 15rem;
  }
`;

const { className: menuClass, styles: menuStyles } = css.resolve`
  .dropdown-menu {
    min-width: 15rem;
  }
`;

const { className: itemClass, styles: itemStyles } = css.resolve`
  .dropdown-item:focus {
    outline: none;
  }
`;

const initState = {
  editingGridLayout: false,
  error: '',
  graphWidgetIDs: [],
  isDraggable: false,
  isResizable: false,
  timeRangeUpdateRealTime: true,
  showCustomFilterModal: false,
  timeFilter: null,
  customStartTimestamp: null,
  customEndTimestamp: null,
  maxDatapoints: null,
};

function AssetDashboardDisplay(props) {
  const {
    asset: { gtemplateID } = {},
    currentDashboard,
    dashboardSearchTerm,
    graphWidgets,
    handleWidgetsUpdate,
    templateDashboards,
    widgets,
  } = props;

  const widgetsWithTimeFilter =
    widgets?.filter((w) => {
      return isTimeFilterVisible(w.type, w.settings?.displayTextAsGrid);
    }) || [];
  const displayGlobalTimeFilter = !!widgetsWithTimeFilter.length;

  const [state, setState] = useState(initState);

  const currentOrg = useContext(CurrentOrgContext),
    socket = useContext(SocketContext);

  const source = useMemo(() => CancelToken.source(), []);

  const filteredDashboards = useMemo(() => {
      return templateDashboards.filter(({ _id, name = '' }) => {
        if (_id === currentDashboard?._id) return false;
        if (!dashboardSearchTerm) return true;

        const prunedName = name.toLowerCase().trim();
        const prunedSearch = dashboardSearchTerm.toLowerCase().trim();
        return prunedName.includes(prunedSearch);
      });
    }, [currentDashboard, dashboardSearchTerm, templateDashboards]),
    graphWidgetIDs = useMemo(
      () => graphWidgets.map(({ _id }) => _id),
      [graphWidgets]
    ),
    sessionStorageService = useMemo(() => new SessionStorageService(), []),
    widgetUpdateHelper = useMemo(() => new WidgetUpdateHelper(), []);

  const handleLayoutChange = useCallback(
      (layout) => {
        sessionStorageService.setItem('gridLayout', layout);
        const layoutDictionary = layout.reduce((acc, l) => {
          acc[l.i] = l;
          return acc;
        }, {});

        const checkForWidgetUpdates = (widget) => {
          if (
            widgetUpdateHelper.widgetHasBeenUpdated(layoutDictionary, widget)
          ) {
            const updatedWidget = widgetUpdateHelper.formWidgetLayoutUpdateBody(
              layoutDictionary,
              widget
            );
            widget = { ...updatedWidget, updated: true };
          }
          return widget;
        };

        const updates = widgets.map(checkForWidgetUpdates);
        handleWidgetsUpdate(updates);
      },
      [handleWidgetsUpdate, sessionStorageService, widgets, widgetUpdateHelper]
    ),
    updateWidgetLayouts = useCallback(async () => {
      try {
        const updatedWidgets = await widgetUpdateHelper.updateGridLayout(
          widgets,
          gtemplateID,
          currentDashboard,
          source.token
        );
        handleWidgetsUpdate(updatedWidgets);
      } catch (err) {
        if (isCancel(err)) return;
        const error = err.response?.data?.message ?? err.message;
        setState((prevState) => ({ ...prevState, error }));
      }
    }, [
      currentDashboard,
      gtemplateID,
      handleWidgetsUpdate,
      source.token,
      widgets,
      widgetUpdateHelper,
    ]),
    handleEditGridToggle = useCallback(
      (nextState) => {
        setState((prevState) => ({
          ...prevState,
          editingGridLayout: nextState,
          isDraggable: nextState,
          isResizable: nextState,
        }));
        if (!nextState) updateWidgetLayouts();
      },
      [updateWidgetLayouts]
    );

  const renderNextGraphWidget = useCallback((widgetID) => {
    setState((prevState) => ({
      ...prevState,
      [`render_${widgetID}`]: true,
    }));
  }, []);

  const handleTimeRangeFilter = useCallback(
    (timeFilter) => {
      if (timeFilter === 'custom') {
        return setState((prevState) => ({
          ...prevState,
          showCustomFilterModal: true,
        }));
      }
      const customEndTimestamp = Date.now(),
        customStartTimestamp = subHours(
          customEndTimestamp,
          timeFilter
        ).getTime();
      setState((prevState) => ({
        ...prevState,
        timeFilter,
        customStartTimestamp,
        customEndTimestamp,
        timeRangeUpdateRealTime: false,
      }));
    },
    [setState]
  );

  const handleReturnToRealTime = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      customEndTimestamp: null,
      customStartTimestamp: null,
      timeFilter: null,
      timeRangeUpdateRealTime: true,
    }));
  }, [setState]);

  const handleCustomFilterSaveCallback = useCallback(
    ({ customEndTimestamp, maxDatapoints, customStartTimestamp }) => {
      setState((prevState) => ({
        ...prevState,
        customEndTimestamp,
        maxDatapoints,
        customStartTimestamp,
        timeFilter: 'custom',
        showCustomFilterModal: false,
        timeRangeUpdateRealTime: false,
      }));
    },
    [setState]
  );

  const toggleCustomFilterModal = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      showCustomFilterModal: !state.showCustomFilterModal,
    }));
  }, [setState, state.showCustomFilterModal]);

  useEffect(() => {
    if (!graphWidgetIDs?.length) return;

    setState((prevState) => ({
      ...prevState,
      graphWidgetIDs,
      [`render_${graphWidgetIDs[0]}`]: true,
    }));
  }, [graphWidgetIDs]);

  useEffect(() => source.cancel, [source.cancel]);

  return (
    <div className="position-absolute container-fluid h-100 overflow-auto">
      <div className="row d-lg-none">
        <div className="col-12">
          <hr className="mb-0" />
        </div>
      </div>
      <div className="row d-flex align-items-center justify-content-between justify-content-md-start my-3 mt-lg-0 three-quarter-text">
        <div className="col-auto">
          <UncontrolledDropdown className="mr-4">
            <Can
              no={() => (
                <DropdownToggle
                  className={`${menuToggleClass} text-text-dark d-flex align-items-center justify-content-between shadow-none px-0 py-1`}
                  color="none"
                  disabled
                >
                  {currentDashboard?.name ?? 'Dashboards'}
                </DropdownToggle>
              )}
              orgID={props.asset?.orgID}
              perform={DASHBOARD_READ}
              resource={DASHBOARD}
              yes={() => (
                <DropdownToggle
                  className={`${menuToggleClass} text-text-dark d-flex align-items-center justify-content-between shadow-none px-0 py-1`}
                  color="none"
                >
                  {currentDashboard?.name ?? 'Dashboards'}
                  <FontAwesomeIcon className="ml-2 pr-0" icon={faChevronDown} />
                </DropdownToggle>
              )}
            />
            <DropdownMenu className={`${menuClass} rounded-lg`}>
              <DropdownItem className="input-group input-group-sm px-2" header>
                <input
                  aria-label="Dashboard Search"
                  aria-describedby="search-icon"
                  className="form-control rounded-lg"
                  name="dashboardSearchTerm"
                  onChange={props.handlePropUpdate}
                  placeholder="Search for dashboards..."
                  type="text"
                  value={dashboardSearchTerm}
                />
                <div className="input-group-append">
                  <span
                    className="input-group-text bg-transparent border-0 pr-0"
                    id="search-icon"
                  >
                    <FontAwesomeIcon
                      className="text-text-light-gray"
                      icon={faSearch}
                    />
                  </span>
                </div>
              </DropdownItem>
              {filteredDashboards.slice(0, 3).map((dashboard, i) => (
                <DropdownItem
                  className="px-2 text-truncate"
                  id={`dashboard-${i}`}
                  key={dashboard._id}
                  onClick={() => props.handleChangeCurrentDashboard(dashboard)}
                >
                  {dashboard.name}
                  <UncontrolledTooltip target={`dashboard-${i}`}>
                    {dashboard.name}
                  </UncontrolledTooltip>
                </DropdownItem>
              ))}
              {filteredDashboards.length > 3 ? (
                <DropdownItem
                  className="px-2 three-quarter-text"
                  onClick={props.toggleViewTemplateDashboardsModal}
                >
                  Show All
                </DropdownItem>
              ) : null}
              <Can
                no={() => null}
                orgID={props.asset?.orgID}
                perform={DASHBOARD_CREATE}
                resource={DASHBOARD}
                yes={() => (
                  <>
                    <DropdownItem className="mt-0" divider />
                    <DropdownItem
                      className={
                        props.asset?.gtemplateID
                          ? `${itemClass} px-2`
                          : 'd-none'
                      }
                      onClick={props.toggleCreateDashboardModal}
                    >
                      <FontAwesomeIcon
                        className="pr-2"
                        icon={faPlus}
                        size="lg"
                      />
                      Create New Dashboard
                    </DropdownItem>
                  </>
                )}
              />
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
        {props.asset?.gtemplateID ? (
          <div
            className={`col-auto ${
              props.currentDashboard?.isPrivate ? '' : 'mr-auto mr-md-3'
            }`}
          >
            <Can
              no={() => null}
              orgID={props.asset?.orgID}
              perform={WIDGET_CREATE}
              resource={DASHBOARD}
              yes={() => (
                <RestrictedCreateButton
                  isDisabled={!props.asset?.gtemplate}
                  toggleCreateWidgetModal={props.toggleCreateWidgetModal}
                />
              )}
            />
          </div>
        ) : null}
        {props.currentDashboard?.isPrivate ? (
          <Can
            no={() => null}
            orgID={props.currentDashboard?.orgID}
            perform={DASHBOARD_UPDATE}
            resource={DASHBOARD}
            yes={() => (
              <div className="col-auto mr-auto mr-md-3">
                <button
                  className="btn btn-sm rounded-lg modal-button-primary py-md-0 px-md-3 three-quarter-text"
                  onClick={props.toggleAddUsersModal}
                >
                  Manage Users
                </button>
              </div>
            )}
          />
        ) : null}
        {props.asset?.gtemplateID ? (
          <div
            className={`d-none ${
              props.currentDashboard?._id
                ? 'd-lg-block mr-lg-auto col-lg-auto'
                : 'd-none'
            }`}
          >
            <Can
              no={() => null}
              orgID={props.asset?.orgID}
              perform={WIDGET_GRID_UPDATE}
              resource={DASHBOARD}
              yes={() => (
                <label
                  htmlFor="edit-grid-toggle"
                  className="mb-0 d-flex align-items-center"
                >
                  <span className="mr-2 font-weight-bold">Edit Grid</span>
                  <RestrictedSwitch
                    editingGridLayout={state.editingGridLayout}
                    handleEditGridToggle={handleEditGridToggle}
                  />
                </label>
              )}
            />
          </div>
        ) : null}

        <TimeRangeDropwdown
          forceDisplay={displayGlobalTimeFilter}
          updateRealTime={state.timeRangeUpdateRealTime}
          handleTimeRangeFilter={handleTimeRangeFilter}
          handleReturnToRealTime={handleReturnToRealTime}
        />

        {currentDashboard?._id ? (
          <Can
            no={() => null}
            orgID={props.currentDashboard?.orgID}
            perform={DASHBOARD_UPDATE}
            resource={DASHBOARD}
            yes={() => (
              <div className="col-auto">
                <button
                  className="btn btn-link p-0"
                  onClick={props.toggleEditDashboardModal}
                >
                  <FontAwesomeIcon icon={faPencilAlt} />
                </button>
              </div>
            )}
          />
        ) : null}
        {currentDashboard?._id && !currentDashboard?.isDefault ? (
          <Can
            no={() => null}
            orgID={props.currentDashboard?.orgID}
            perform={DASHBOARD_DELETE}
            resource={DASHBOARD}
            yes={() => (
              <div className="col-auto pl-0">
                <button
                  className="btn btn-link text-decoration-none shadow-none text-danger p-0"
                  onClick={props.toggleDeleteDashboardModal}
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                </button>
              </div>
            )}
          />
        ) : null}
      </div>
      <div
        className={
          state.editingGridLayout ? 'row justify-content-end' : 'd-none'
        }
      >
        <p className="col-auto text-right text-muted bg-white rounded mb-2">
          Click and drag widgets to move them. Drag the corners to resize them.
          Click the switch again to save!
        </p>
      </div>
      <div className="row">
        <div className="col-12">
          {(() => {
            switch (true) {
              case !!props.error:
                return (
                  <div className="h-100 w-100 d-flex align-items-center justify-content-center">
                    <p className="mb-0 text-danger three-quarter-text">
                      {props.error}
                    </p>
                  </div>
                );
              case !!props.layout.length:
                return (
                  <ResponsiveGridLayout
                    breakpoints={props.breakpoints}
                    cols={props.cols}
                    draggableHandle=".draggable"
                    layouts={{
                      xs: props.layout,
                      sm: props.layout,
                      md: props.layout,
                      lg: props.layout,
                      xl: props.layout,
                    }}
                    onLayoutChange={handleLayoutChange}
                    onResizeStart={handleWidgetResizeStart}
                    onResizeStop={handleWidgetResizeStop}
                    rowHeight={300}
                    verticalCompact={true}
                  >
                    {props.nonGraphWidgets?.map?.((w) => (
                      <div
                        className={`bg-white widget-outer-container ${
                          state.isDraggable ? 'draggable' : 'not-draggable'
                        }`}
                        id={w._id}
                        key={w._id}
                      >
                        <WidgetDisplayContainer
                          asset={props.asset}
                          dashboardID={currentDashboard._id}
                          fetchCustomRangeUtilizationData={
                            props.fetchCustomRangeUtilizationData
                          }
                          fetchedData={props.fetchedData}
                          handleCommandExecutedFromWidget={
                            props.handleCommandExecutedFromWidget
                          }
                          handleWidgetDeleted={props.handleWidgetDeleted}
                          handleWidgetUpdated={props.handleWidgetUpdated}
                          historicalUtilizationData={
                            props.historicalUtilizationData
                          }
                          signalNamesMap={props.signalNamesMap}
                          signalUnitsMap={props.signalUnitsMap}
                          socket={socket}
                          updateWidgets={props.updateWidgets}
                          utilizationData={props.utilizationData}
                          widget={w}
                          timeFilter={state.timeFilter}
                          customStartTimestamp={state.customStartTimestamp}
                          customEndTimestamp={state.customEndTimestamp}
                          updateRealTime={state.timeRangeUpdateRealTime}
                          maxDatapoints={state.maxDatapoints}
                        />
                      </div>
                    )) || null}
                    {props.graphWidgets?.map?.((w, i) => (
                      <div
                        className={`bg-white widget-outer-container ${
                          state.isDraggable ? 'draggable' : 'not-draggable'
                        }`}
                        id={w._id}
                        key={w._id}
                      >
                        {state[`render_${w._id}`] ? (
                          <WidgetDisplayContainer
                            asset={props.asset}
                            dashboardID={currentDashboard._id}
                            fetchCustomRangeUtilizationData={
                              props.fetchCustomRangeUtilizationData
                            }
                            fetchedData={props.fetchedData}
                            graphIndex={i}
                            graphWidgetIDs={state.graphWidgetIDs}
                            handleWidgetDeleted={props.handleWidgetDeleted}
                            handleWidgetUpdated={props.handleWidgetUpdated}
                            historicalUtilizationData={
                              props.historicalUtilizationData
                            }
                            renderNextGraph={renderNextGraphWidget}
                            signalNamesMap={props.signalNamesMap}
                            signalUnitsMap={props.signalUnitsMap}
                            socket={socket}
                            updateWidgets={props.updateWidgets}
                            utilizationData={props.utilizationData}
                            widget={w}
                            timeFilter={state.timeFilter}
                            customStartTimestamp={state.customStartTimestamp}
                            customEndTimestamp={state.customEndTimestamp}
                            updateRealTime={state.timeRangeUpdateRealTime}
                            maxDatapoints={state.maxDatapoints}
                          />
                        ) : (
                          <div className="row h-100 align-items-center justify-content-center">
                            <FontAwesomeIcon
                              color="var(--brand-primary)"
                              icon={faCircleNotch}
                              size="2x"
                              spin
                            />
                          </div>
                        )}
                      </div>
                    )) || null}
                  </ResponsiveGridLayout>
                );
              default:
                return (
                  <div
                    className="d-flex align-items-center justify-content-center mb-5 mb-md-0"
                    style={{ height: '15rem' }}
                  >
                    <NoItemsExist
                      alt="empty state"
                      text="No smart tiles on this dashboard yet."
                    />
                  </div>
                );
            }
          })()}
        </div>
      </div>
      {state.showCustomFilterModal ? (
        <CustomTimeFilterModal
          customEndTimestamp={state.customEndTimestamp}
          customStartTimestamp={state.customStartTimestamp}
          showCustomFilterModal={state.showCustomFilterModal}
          handleCustomFilterSaveCallback={handleCustomFilterSaveCallback}
          toggleCustomFilterModal={toggleCustomFilterModal}
        />
      ) : null}
      {props.showAddUsersModal ? (
        <AddDashboardUsersModal
          dashboard={props.currentDashboard}
          handleAddUsersSave={props.handleAddUsersSave}
          isOpen={props.showAddUsersModal}
          toggleModal={props.toggleAddUsersModal}
        />
      ) : null}
      {props.showCreateDashboardModal ? (
        <CreateTemplateDashboardModal
          assetID={props.asset?._id}
          gtemplateID={props.asset?.gtemplateID}
          handleDashboardCreated={props.handleDashboardCreated}
          isOpen={props.showCreateDashboardModal}
          orgID={props.asset?.orgID}
          toggleModal={props.toggleCreateDashboardModal}
        />
      ) : null}
      {props.showCreateWidgetModal ? (
        <CreateWidgetModal
          asset={props.asset}
          currentOrg={currentOrg}
          dashboardID={currentDashboard._id}
          isOpen={props.showCreateWidgetModal}
          toggleModal={props.toggleCreateWidgetModal}
          updateWidgets={props.handleWidgetAdded}
        />
      ) : null}
      {props.showDeleteDashboardModal ? (
        <DeleteTemplateDashboardModal
          dashboard={currentDashboard}
          gtemplateID={props.asset?.gtemplateID}
          handleDashboardDeleted={props.handleTemplateDashboardDeleted}
          isOpen={props.showDeleteDashboardModal}
          toggleModal={props.toggleDeleteDashboardModal}
        />
      ) : null}
      {props.showEditDashboardModal ? (
        <EditDashboardModal
          assetID={props.asset?._id}
          dashboard={currentDashboard}
          handleEditDashboardSave={props.handleEditDashboardSave}
          isOpen={props.showEditDashboardModal}
          toggleModal={props.toggleEditDashboardModal}
        />
      ) : null}
      {props.showViewTemplateDashboardsModal ? (
        <ViewTemplateDashboardsModal
          dashboardSearchTerm={dashboardSearchTerm}
          handleDashboardSelected={props.handleChangeCurrentDashboard}
          isOpen={props.showViewTemplateDashboardsModal}
          templateDashboards={templateDashboards}
          toggleModal={props.toggleViewTemplateDashboardsModal}
        />
      ) : null}

      {menuToggleStyles}
      {menuStyles}
      {itemStyles}
      <style jsx>{`
        .btn:focus {
          outline: none !important;
        }
        .widget-outer-container {
          border-radius: 1rem;
        }
      `}</style>
      <style jsx global>{`
        .react-resizable-handle {
          bottom: 0;
          cursor: se-resize;
          display: ${state.isResizable ? 'block' : 'none'};
          height: 20px;
          right: 0;
          position: absolute;
          width: 20px;
        }
        .react-resizable-handle:after {
          border-bottom: 5px solid var(--alert-confirmation);
          border-radius: 3px;
          border-right: 5px solid var(--alert-confirmation);
          bottom: 5px;
          content: '';
          height: 20px;
          position: absolute;
          right: 5px;
          width: 20px;
        }
      `}</style>
    </div>
  );
}

AssetDashboardDisplay.propTypes = {
  asset: PropTypes.shape({
    _id: PropTypes.string,
    gtemplate: PropTypes.object,
    gtemplateID: PropTypes.string,
    orgID: PropTypes.string,
  }),
  breakpoints: PropTypes.shape({
    xs: PropTypes.number,
    sm: PropTypes.number,
    md: PropTypes.number,
    lg: PropTypes.number,
    xl: PropTypes.number,
  }),
  cols: PropTypes.shape({
    xs: PropTypes.number,
    sm: PropTypes.number,
    md: PropTypes.number,
    lg: PropTypes.number,
    xl: PropTypes.number,
  }),
  currentDashboard: PropTypes.shape({
    _id: PropTypes.string,
    gtemplateID: PropTypes.string,
    isDefault: PropTypes.bool,
    isPrivate: PropTypes.bool,
    name: PropTypes.string,
    orgID: PropTypes.string,
    widgets: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
        col: PropTypes.number,
        row: PropTypes.number,
        settings: PropTypes.object,
        sizeX: PropTypes.number,
        sizeY: PropTypes.number,
      })
    ),
  }),
  dashboardSearchTerm: PropTypes.string,
  dataLoading: PropTypes.bool,
  error: PropTypes.string,
  fetchCustomRangeUtilizationData: PropTypes.func,
  fetchedData: PropTypes.object,
  graphWidgets: PropTypes.arrayOf(PropTypes.object),
  handleAddUsersSave: PropTypes.func,
  handleChangeCurrentDashboard: PropTypes.func,
  handleCommandExecutedFromWidget: PropTypes.func,
  handleDashboardCreated: PropTypes.func,
  handleEditDashboardSave: PropTypes.func,
  handlePropUpdate: PropTypes.func,
  handleTemplateDashboardDeleted: PropTypes.func,
  handleWidgetAdded: PropTypes.func,
  handleWidgetDeleted: PropTypes.func,
  handleWidgetUpdated: PropTypes.func,
  handleWidgetsUpdate: PropTypes.func,
  historicalUtilizationData: PropTypes.object,
  layout: PropTypes.arrayOf(
    PropTypes.shape({
      i: PropTypes.string,
      x: PropTypes.number,
      y: PropTypes.number,
      h: PropTypes.number,
      w: PropTypes.number,
    })
  ),
  nonGraphWidgets: PropTypes.arrayOf(PropTypes.object),
  showAddUsersModal: PropTypes.bool,
  showCreateDashboardModal: PropTypes.bool,
  showCreateWidgetModal: PropTypes.bool,
  showDeleteDashboardModal: PropTypes.bool,
  showEditDashboardModal: PropTypes.bool,
  showViewTemplateDashboardsModal: PropTypes.bool,
  signalNamesMap: PropTypes.object,
  signalUnitsMap: PropTypes.object,
  templateDashboards: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      gtemplateID: PropTypes.string,
      isDefault: PropTypes.bool,
      name: PropTypes.string,
      orgID: PropTypes.string,
      widgets: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string,
          col: PropTypes.number,
          row: PropTypes.number,
          settings: PropTypes.object,
          sizeX: PropTypes.number,
          sizeY: PropTypes.number,
        })
      ),
    })
  ),
  toggleAddUsersModal: PropTypes.func,
  toggleCreateDashboardModal: PropTypes.func,
  toggleCreateWidgetModal: PropTypes.func,
  toggleDeleteDashboardModal: PropTypes.func,
  toggleEditDashboardModal: PropTypes.func,
  toggleViewTemplateDashboardsModal: PropTypes.func,
  updateWidgets: PropTypes.func,
  utilizationData: PropTypes.object,
  widgets: PropTypes.arrayOf(PropTypes.object),
};
AssetDashboardDisplay.defaultProps = {
  graphWidgets: [],
  layout: [],
  templateDashboards: [],
};

function RestrictedCreateButton(props) {
  const conditionallyDisabled = props.isDisabled ? { disabled: true } : {};

  return (
    <button
      {...conditionallyDisabled}
      className="btn btn-sm rounded-lg modal-button-primary py-md-0 px-md-3 three-quarter-text d-flex align-items-center"
      onClick={props.toggleCreateWidgetModal}
    >
      <FontAwesomeIcon icon={faPlus} />
      <span className="d-none d-md-block ml-md-2">New Smart Tile</span>
      <style jsx>{`
        .btn:focus {
          outline: none;
        }
      `}</style>
    </button>
  );
}

RestrictedCreateButton.propTypes = {
  isDisabled: PropTypes.bool,
  toggleCreateWidgetModal: PropTypes.func,
};

function RestrictedSwitch(props) {
  const { primary_alert } = useContext(BrandingContext);
  const conditionallyDisabled = props.isDisabled ? { disabled: true } : {};

  return (
    <Switch
      {...conditionallyDisabled}
      onChange={props.handleEditGridToggle}
      checked={props.editingGridLayout}
      checkedIcon={false}
      uncheckedIcon={false}
      height={15}
      width={30}
      onColor={primary_alert}
      id="edit-grid-toggle"
    />
  );
}
RestrictedSwitch.propTypes = {
  editingGridLayout: PropTypes.bool,
  handleEditGridToggle: PropTypes.func,
  isDisabled: PropTypes.bool,
};
RestrictedSwitch.defaultProps = { handleEditGridToggle: () => {} };

function handleWidgetResizeStart(...allArgs) {
  const detail = {
    detail: { resize: true, id: allArgs[1].i },
  };
  const event = new CustomEvent('resize', detail);
  window.dispatchEvent(event);
}

function handleWidgetResizeStop(...allArgs) {
  const detail = {
    detail: { resize: false, id: allArgs[1].i },
  };
  const event = new CustomEvent('resize', detail);
  window.dispatchEvent(event);
}
